// Steering board — "Your requests", grouped by stage.
//
// Direct port of `loom/project/steerboard.jsx` `SteerBoard`. Groups in
// `DASHBOARD_STAGES` order; renders a group only when it has at least
// one issue. The filter is "Active" (default) or "All" — Active hides
// the `shipped` stage. A declined proposal (closed while still
// `loom::proposal-pending`, `isDeclinedProposal`) never shipped, so it is in
// neither: the `shipped` count is work that shipped.

import type { JSX } from "react";

import { isDeclinedProposal } from "@loom/shared/config";
import {
  DASHBOARD_STAGES,
  type DashboardIssue,
  type DashboardStage,
} from "@loom/shared/dashboard";

import { useT } from "../../i18n/index.js";

import { Icon } from "./Icon";
import { Spinner } from "./Spinner";
import { SteerGroup } from "./SteerGroup";

export type SteerFilter = "active" | "all";

interface SteerBoardProps {
  readonly issues: ReadonlyArray<DashboardIssue>;
  readonly loading?: boolean;
  readonly filter: SteerFilter;
  readonly onFilterChange: (next: SteerFilter) => void;
  readonly onNew: () => void;
  readonly onEdit: (issue: DashboardIssue) => void;
  readonly onChat: (issue: DashboardIssue) => void;
}

export const SteerBoard = ({
  issues,
  loading,
  filter,
  onFilterChange,
  onNew,
  onEdit,
  onChat,
}: SteerBoardProps): JSX.Element => {
  const t = useT();
  const visible = issues.filter(
    (i) =>
      !isDeclinedProposal(i.labels, i.stage === "shipped" || i.closedAt !== null) &&
      (filter === "all" ? true : i.stage !== "shipped"),
  );

  const groups: Array<{ stage: DashboardStage; items: DashboardIssue[] }> =
    DASHBOARD_STAGES.map((stage) => ({
      stage,
      items: visible.filter((i) => i.stage === stage),
    })).filter((g) => g.items.length > 0);

  return (
    <section className="steer">
      <div className="steer-head">
        <div className="steer-head-left">
          <span className="steer-eyebrow">
            <Icon name="sliders" size={12} /> {t("dashboard.steer.eyebrow")}
          </span>
          <h2 className="steer-title">{t("dashboard.steer.title")}</h2>
          <span className="steer-sub">
            {t("dashboard.steer.sub")}
          </span>
        </div>
        <div className="steer-head-right">
          <div
            className="seg"
            role="group"
            aria-label={t("dashboard.steer.filterRequests")}
            title={t("dashboard.steer.filterTitle")}
          >
            <button
              className={filter === "active" ? "on" : ""}
              onClick={() => onFilterChange("active")}
            >
              {t("dashboard.steer.active")}
            </button>
            <button
              className={filter === "all" ? "on" : ""}
              onClick={() => onFilterChange("all")}
            >
              {t("dashboard.steer.all")}
            </button>
          </div>
          <button className="steer-new" onClick={onNew}>
            <Icon name="plus" size={13} /> {t("dashboard.steer.newRequest")}
          </button>
        </div>
      </div>

      <div className="steer-groups">
        {loading && issues.length === 0 ? (
          <div style={{ padding: "28px 20px", display: "flex", justifyContent: "center" }}>
            <Spinner size={18} label={t("dashboard.steer.loadingRequests")} />
          </div>
        ) : (
          groups.map(({ stage, items }) => (
            <SteerGroup
              key={stage}
              stage={stage}
              issues={items}
              onEdit={onEdit}
              onChat={onChat}
            />
          ))
        )}
      </div>
    </section>
  );
};
